/**
 * Utility functions for handling products in the rebooking process
 */
import { RebookingAdditionalProduct } from '../../types/rebookingTypes'
import { v4 as uuidv4 } from 'uuid'

export const isNotCancelled = (product: any): boolean => {
  if (!product) return false;
  return !product.cancelled && !product.cancellation_requested
}

/**
 * Gets the service name for a product
 * 
 * @param product The product or object containing service information
 * @param bookingData Optional booking data to look up segment service info if product lacks it
 * @returns The extracted service name or undefined
 */
export const getProductServiceName = (product: any, bookingData?: any): string | undefined => {
  const itemId = product?.item_id || product?.id || 'unknown';
  const productCode = product?.code || product?.product_code || 'unknown';
  
  if (!product) {
    return undefined;
  }

  if (product.service_name) {
    return product.service_name;
  }

  if (product.service?.name) {
    return product.service.name;
  }
  
  if (product.service_identifier) {
    const parts = product.service_identifier.split('|');
    
    if (parts.length >= 2) {
      return parts[1];
    }
  }
  
  // Try to get service information from the associated segment if bookingData is provided
  if (bookingData && (product.uuid || product.id || product.item_id)) {
    const productId = product.uuid || product.id || product.item_id;
    
    const segment = findOriginalSegment(productId, bookingData);
    if (segment) {
      // Try to extract service information from the segment
      if (segment.service_name) {
        return segment.service_name;
      }
      
      if (segment.service?.name) {
        return segment.service.name;
      }
      
      if (segment.service_identifier) {
        const parts = segment.service_identifier.split('|');
        
        if (parts.length >= 2) {
          return parts[1];
        }
      }
      
      // If the segment has a required product with service info, use that
      if (Array.isArray(segment.required_products) && segment.required_products.length > 0) {
        const mainProduct = segment.required_products[0];
        if (mainProduct?.service_name) {
          return mainProduct.service_name;
        }
        
        if (mainProduct?.service?.name) {
          return mainProduct.service.name;
        }
        
        if (mainProduct?.service_identifier) {
          const parts = mainProduct.service_identifier.split('|');
          if (parts.length >= 2) {
            return parts[1];
          }
        }
      }
    }
  }
  
  return undefined;
}

/**
 * Gets the service identifier for a product
 */
export const getProductServiceIdentifier = (product: any): string | undefined => {
  if (!product) return undefined

  if (product.service_identifier) {
    return product.service_identifier
  }

  if (product.service?.identifier) {
    return product.service.identifier
  }

  return undefined
}

/**
 * Checks if a product is available for a service in the new journey
 * with improved flexibility for service and product code matching
 */
export const isProductAvailableForService = (
  product: any,
  newJourneyProductsByService: Map<string, Set<string>>,
  bookingData?: any
): boolean => {
  const itemId = product?.item_id || product?.id || 'unknown';
  
  if (!product) {
    return false;
  }
  
  if (!newJourneyProductsByService || !(newJourneyProductsByService instanceof Map)) {
    return false;
  }
  
  // Use the extracted service name if available, otherwise try to get it again
  const serviceId = product._extracted_service_name || getProductServiceName(product, bookingData);
  const productCode = product.code || product.product_code;
  
  // If we don't have a product code, we can't check availability
  if (!productCode) {
    return false;
  }
  
  // If we have a service ID, check if the product is available for that service
  if (serviceId) {
    const availableProducts = newJourneyProductsByService.get(serviceId);
    
    if (availableProducts) {
      const isAvailable = availableProducts.has(productCode);
      
      if (isAvailable) {
        return true;
      }
    }
  }
  
  // If we don't have a service ID or the product is not available for the specified service,
  // check if the product is available for any service
  let isAvailableInAnyService = false;
  newJourneyProductsByService.forEach((products, service) => {
    if (products.has(productCode)) {
      isAvailableInAnyService = true;
    }
  });
  
  if (isAvailableInAnyService) {
    return true;
  }
  
  return false;
}

/**
 * Extracts product codes from travel data
 */
export const extractProductCodes = (
  travelData: any, 
  journeyType: 'outbound' | 'inbound',
  newJourneyProductsByService: Map<string, Set<string>>
) => {
  try {
    if (!travelData) {
      console.warn(`[WARN] extractProductCodes called with null/undefined travelData for ${journeyType}`)
      return
    }
    
    if (!Array.isArray(travelData.routes)) {
      console.warn(`[WARN] travelData.routes is not an array in ${journeyType}:`, 
        typeof travelData.routes, travelData.routes)
      return
    }
    
    if (!newJourneyProductsByService || typeof newJourneyProductsByService.has !== 'function') {
      return
    }

    travelData.routes.forEach((route: any, routeIndex: number) => {
      if (!route) {
        return
      }
      
      if (!Array.isArray(route.legs)) {
        return
      }
      
      if (route.legs.length === 0) {
        return
      }
      
      const firstLeg = route.legs[0]
      if (!firstLeg) {
        return
      }
      
      const serviceId = firstLeg.service_name
      if (!serviceId) {
        return
      }
      
      if (!newJourneyProductsByService.has(serviceId)) {
        newJourneyProductsByService.set(serviceId, new Set<string>())
      }

      if (!Array.isArray(route.bundles)) {
        console.warn(`[WARN] route.bundles is not an array in ${journeyType} route ${routeIndex}:`, 
          typeof route.bundles, route.bundles)
        return
      }
      
      route.bundles.forEach((bundle: any, bundleIndex: number) => {
        if (!bundle) {
          console.warn(`[WARN] Bundle at index ${bundleIndex} is null/undefined in ${journeyType} route ${routeIndex}`)
          return
        }
        
        if (!Array.isArray(bundle.items)) {
          console.warn(`[WARN] bundle.items is not an array in ${journeyType} route ${routeIndex}, bundle ${bundleIndex}:`, 
            typeof bundle.items, bundle.items)
          return 
        }

        bundle.items.forEach((item: any, itemIndex: number) => {
          if (!item) {
            console.warn(`[WARN] Item at index ${itemIndex} is null/undefined in ${journeyType} route ${routeIndex}, bundle ${bundleIndex}`)
            return
          }
          
          if (!item.product_code) {
            console.warn(`[WARN] Missing product_code in item at index ${itemIndex} in ${journeyType} route ${routeIndex}, bundle ${bundleIndex}`)
            return
          }
          
          const productSet = newJourneyProductsByService.get(serviceId)
          if (!productSet || typeof productSet.add !== 'function') {
            console.error(`[ERROR] Invalid product set for service ${serviceId} in ${journeyType}`)
            return
          }
          
          productSet.add(item.product_code)
        })
      })
    })
  } catch (error) {
    console.error(`[ERROR] Exception in extractProductCodes for ${journeyType}:`, error)
  }
}

/**
 * Gets complete product information from various data sources
 * Searches through original booking data and selected travel data to find product information
 */
export interface ProductInfo {
  tariffCode: string;
  productCode: string;
  productType: string;
  price?: number;
  source: string;
}

/**
 * Gets original product information for a specific passenger and segment direction
 * This ensures we use the exact values from the original booking
 */
export const getOriginalProductInfoByPassenger = (
  bookingData: any,
  passengerRefId: string,
  segmentDirection: 'OUTBOUND' | 'INBOUND',
  isMainProduct: boolean = true
): ProductInfo | null => {
  if (!bookingData || !passengerRefId) {
    console.warn(`[WARN] Missing booking data or passenger reference ID in getOriginalProductInfoByPassenger`)
    return null
  }

  const segments = segmentDirection === 'OUTBOUND'
    ? bookingData.outbound_booking_tariff_segments || []
    : bookingData.inbound_booking_tariff_segments || []
  
  const passenger = Array.isArray(bookingData.passengers)
    ? bookingData.passengers.find((p: any) => String(p.id) === passengerRefId)
    : null
  
  if (!passenger) {
    console.warn(`[WARN] Could not find passenger with ref ID ${passengerRefId} in booking data`)
    return null
  }
  
  const passengerUuid = passenger.uuid
  
  // Search for products in the segments
  for (const segment of segments) {
    if (isMainProduct) {
      // Look for main products
      if (Array.isArray(segment.required_products)) {
        for (const product of segment.required_products) {
          if (product.passenger_uuid === passengerUuid || product.passenger_id === passengerUuid) {
            // Found original main product for passenger
            return {
              tariffCode: product.tariff_code || '',
              productCode: product.product_code || product.code || '',
              productType: product.product_type || product.type || 'ST',
              price: product.price,
              source: `${segmentDirection.toLowerCase()}_required_product_by_passenger`
            }
          }
        }
      }
    } else {
      // Look for additional products
      if (Array.isArray(segment.additional_products)) {
        for (const product of segment.additional_products) {
          if (product.passenger_uuid === passengerUuid || product.passenger_id === passengerUuid) {
            // Found original additional product for passenger
            return {
              tariffCode: product.tariff_code || '',
              productCode: product.product_code || product.code || '',
              productType: product.product_type || product.type || '',
              price: product.price,
              source: `${segmentDirection.toLowerCase()}_additional_product_by_passenger`
            }
          }
        }
      }
    }
  }
  
  // If we couldn't find an exact match, log a warning
  console.warn(`[WARN] Could not find original ${isMainProduct ? 'main' : 'additional'} product for passenger ${passengerRefId} in ${segmentDirection} segment`)
  return null
}

export const getProductInfo = (
  itemId: string,
  bookingData: any,
  selectedTravel: any
): ProductInfo | null => {
  // 1. Try to find in original booking outbound segments
  if (bookingData?.outbound_booking_tariff_segments) {
    for (const segment of bookingData.outbound_booking_tariff_segments) {
      // Check required products
      if (segment?.required_products) {
        const product = segment.required_products.find(
          (p: any) => p.uuid === itemId || p.id === itemId
        )
        if (product) {
          return {
            tariffCode: product.tariff_code,
            productCode: product.code || product.product_code,
            productType: product.type,
            price: product.price,
            source: 'outbound_required_product'
          }
        }
      }
      
      // Check additional products
      if (segment?.additional_products) {
        const product = segment.additional_products.find(
          (p: any) => p.uuid === itemId || p.id === itemId
        )
        if (product) {
          return {
            tariffCode: product.tariff_code,
            productCode: product.code || product.product_code,
            productType: product.type,
            price: product.price,
            source: 'outbound_additional_product'
          }
        }
      }
    }
  }
  
  // 2. Try to find in original booking inbound segments
  if (bookingData?.inbound_booking_tariff_segments) {
    for (const segment of bookingData.inbound_booking_tariff_segments) {
      // Check required products
      if (segment?.required_products) {
        const product = segment.required_products.find(
          (p: any) => p.uuid === itemId || p.id === itemId
        )
        if (product) {
          return {
            tariffCode: product.tariff_code,
            productCode: product.code || product.product_code,
            productType: product.type,
            price: product.price,
            source: 'inbound_required_product'
          }
        }
      }
      
      // Check additional products
      if (segment?.additional_products) {
        const product = segment.additional_products.find(
          (p: any) => p.uuid === itemId || p.id === itemId
        )
        if (product) {
          return {
            tariffCode: product.tariff_code,
            productCode: product.code || product.product_code,
            productType: product.type,
            price: product.price,
            source: 'inbound_additional_product'
          }
        }
      }
    }
  }
  
  // 3. Try to find in selected travel data
  if (selectedTravel?.routes) {
    for (const route of selectedTravel.routes) {
      if (route?.bundles) {
        for (const bundle of route.bundles) {
          if (bundle?.items) {
            for (const item of bundle.items) {
              if (item.id === itemId) {
                // Found the item in selected travel
                if (item.passenger_fares && item.passenger_fares.length > 0) {
                  const fare = item.passenger_fares[0];
                  return {
                    tariffCode: fare.tariff_code,
                    productCode: fare.product_code || item.product_code,
                    productType: item.product_type || item.type,
                    price: fare.price,
                    source: 'selected_travel_item'
                  }
                } else {
                  return {
                    tariffCode: item.tariff_code,
                    productCode: item.product_code,
                    productType: item.product_type || item.type,
                    source: 'selected_travel_item_no_fare'
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  
  // 4. Try to find default product info from segments
  let segmentInfo = null;
  
  // Try outbound segment first
  if (bookingData?.outbound_booking_tariff_segments && bookingData.outbound_booking_tariff_segments.length > 0) {
    const segment = bookingData.outbound_booking_tariff_segments[0];
    if (segment?.required_products && segment.required_products.length > 0) {
      const product = segment.required_products[0];
      segmentInfo = {
        tariffCode: product.tariff_code,
        productCode: product.code || product.product_code,
        productType: product.type,
        source: 'outbound_segment_default'
      };
    } else if (segment?.tariff_group_code) {
      segmentInfo = {
        tariffCode: segment.tariff_group_code,
        productCode: segment.tariff_group_code,
        productType: 'ST', // Standard product type as last resort
        source: 'outbound_segment_tariff_group'
      };
    }
  }
  
  // If we found segment info, return it
  if (segmentInfo) {
    console.log(`Using segment-derived product info as fallback: ${segmentInfo.source}`);
    return segmentInfo;
  }
  
  // 5. Try to get from selected travel as last resort
  if (selectedTravel?.routes && selectedTravel.routes.length > 0) {
    const route = selectedTravel.routes[0];
    if (route?.bundles && route.bundles.length > 0) {
      const bundle = route.bundles[0];
      if (bundle?.items && bundle.items.length > 0) {
        const item = bundle.items[0];
        if (item.passenger_fares && item.passenger_fares.length > 0) {
          const fare = item.passenger_fares[0];
          return {
            tariffCode: fare.tariff_code,
            productCode: fare.product_code || item.product_code,
            productType: item.product_type || item.type || 'ST',
            price: fare.price,
            source: 'selected_travel_default'
          };
        }
      }
    }
  }
  
  // If we get here, we couldn't find any product info
  console.error(`Failed to find product information for item ${itemId}`);
  return null;
}

/**
 * Finds a bundle by family
 */
export const findBundleByFamily = (bundles: any[]) => {
  if (!bundles || !bundles.length) return null
  
  // First try to find a bundle with the same family
  const bundle = bundles.find(b => b.family === 'STANDARD')
  if (bundle) return bundle
  
  // If no bundle with the same family is found, return the first bundle
  return bundles[0]
}

/**
 * Gets a consistent reference ID for a product
 */
export const getConsistentRefId = (productUuid: string) => {
  return `product_${productUuid.replace(/-/g, '')}`
}

/**
 * Collects IDs of items to be replaced
 */
export const collectReplacedIds = (itemsWithFares: any[] | null | undefined) => {
  const replacedIds: string[] = []
  
  if (!itemsWithFares || !Array.isArray(itemsWithFares) || itemsWithFares.length === 0) {
    return replacedIds
  }
  
  itemsWithFares.forEach(item => {
    if (!item) return
    // Check for uuid first, then id as fallback
    if (item.uuid) {
      replacedIds.push(item.uuid)
    } else if (item.id) {
      replacedIds.push(item.id)
    }
  })
  
  return replacedIds
}

/**
 * Collects IDs of items to be cancelled and adds them to the API request
 */
export const collectCancelIds = (apiRequest: any, bookingData: any, itemsToReplace: string[] = []) => {
  console.log('[DEBUG] Starting collectCancelIds function')
  console.log('[DEBUG] itemsToReplace:', itemsToReplace)
  
  // If specific items are being replaced, only cancel those
  if (Array.isArray(itemsToReplace) && itemsToReplace.length > 0) {
    console.log('[DEBUG] Using explicit itemsToReplace as cancel_item_ids:', itemsToReplace)
    apiRequest.item_adjustments.cancel_item_ids = [...itemsToReplace]
    return
  }
  
  // Get all items that are being replaced
  const replacementItemIds = new Set()
  if (apiRequest?.item_adjustments?.replacements && Array.isArray(apiRequest.item_adjustments.replacements)) {
    console.log('[DEBUG] Found replacements array with length:', apiRequest.item_adjustments.replacements.length)
    
    apiRequest.item_adjustments.replacements.forEach(
      (replacement: {
        existing_item_ids: string[]
        added_item_ref_ids: string[]
      }, index: number) => {
        console.log(`[DEBUG] Processing replacement mapping ${index + 1}:`, replacement)
        
        if (replacement?.existing_item_ids && Array.isArray(replacement.existing_item_ids)) {
          replacement.existing_item_ids.forEach((id) => {
            replacementItemIds.add(id)
            console.log(`[DEBUG] Added item to replacementItemIds: ${id}`)
          })
        }
      }
    )
  } else {
    console.log('[DEBUG] No replacements array found in apiRequest.item_adjustments')
  }
  
  console.log('[DEBUG] Total items in replacementItemIds:', replacementItemIds.size)
  
  // Otherwise, collect all items to cancel
  const outboundSegments = bookingData.outbound_booking_tariff_segments || []
  const inboundSegments = bookingData.inbound_booking_tariff_segments || []
  
  console.log('[DEBUG] Found outbound segments:', outboundSegments.length)
  console.log('[DEBUG] Found inbound segments:', inboundSegments.length)
  
  const collectIdsFromSegments = (segments: any[] | undefined, direction: string) => {
    const cancelIds: string[] = []
    
    if (!segments || !Array.isArray(segments)) {
      console.log(`[DEBUG] No valid segments found for ${direction}`)
      return cancelIds
    }
    
    segments.forEach((segment, segmentIndex) => {
      console.log(`[DEBUG] Processing ${direction} segment ${segmentIndex}:`, segment.id || 'unknown')
      
      if (segment?.required_products && Array.isArray(segment.required_products)) {
        console.log(`[DEBUG] Found ${segment.required_products.length} required products in segment`)
        
        segment.required_products.forEach((product: any, productIndex: number) => {
          console.log(`[DEBUG] Examining product ${productIndex}:`, {
            id: product?.id,
            uuid: product?.uuid,
            cancelled: product?.cancelled,
            status: product?.status
          })
          
          // IMPORTANT: Check both id and uuid, and log which one is being used
          if (product) {
            const itemId = product.uuid || product.id
            const isCancelled = product.cancelled === true || product.status === 'cancelled'
            
            console.log(`[DEBUG] Product ${productIndex} - Using ${product.uuid ? 'uuid' : 'id'}: ${itemId}, isCancelled: ${isCancelled}`)
            
            if (itemId && !isCancelled) {
              cancelIds.push(itemId)
              console.log(`[DEBUG] Added item to cancelIds: ${itemId}`)
            } else if (!itemId) {
              console.log(`[WARNING] Product ${productIndex} has no valid id or uuid`)
            } else if (isCancelled) {
              console.log(`[DEBUG] Skipping already cancelled product: ${itemId}`)
            }
          } else {
            console.log(`[WARNING] Null or undefined product at index ${productIndex}`)
          }
        })
      } else {
        console.log(`[DEBUG] No required_products found in ${direction} segment ${segmentIndex}`)
      }
    })
    
    console.log(`[DEBUG] Collected ${cancelIds.length} cancel IDs from ${direction} segments`)
    return cancelIds
  }
  
  const outboundCancelIds = collectIdsFromSegments(outboundSegments, 'outbound')
  const inboundCancelIds = collectIdsFromSegments(inboundSegments, 'inbound')

  // Filter out any items that are already in replacements
  const combinedIds = [
    ...(Array.isArray(outboundCancelIds) ? outboundCancelIds : []),
    ...(Array.isArray(inboundCancelIds) ? inboundCancelIds : [])
  ]
  
  console.log('[DEBUG] Combined cancel IDs before filtering:', combinedIds.length)
  
  if (apiRequest?.item_adjustments) {
    const filteredIds = Array.isArray(combinedIds) ? 
      combinedIds.filter((id) => id && !replacementItemIds.has(id)) : 
      [];
    
    apiRequest.item_adjustments.cancel_item_ids = filteredIds;
    
    console.log('[DEBUG] Final cancel_item_ids after filtering:', filteredIds.length)
    console.log('[DEBUG] Items removed by filtering:', combinedIds.length - filteredIds.length)
    
    // Log the actual IDs for debugging
    if (filteredIds.length > 0) {
      console.log('[DEBUG] Final cancel_item_ids:', filteredIds)
    }
  } else {
    console.log('[WARNING] apiRequest.item_adjustments is undefined, cannot set cancel_item_ids')
  }
}

/**
 * Helper function to find the original item in booking data
 */
export const findOriginalItem = (itemId: string, bookingData: any) => {
  let originalItem = null
  
  // Look for the original item in the booking data outbound segments
  if (bookingData?.outbound_booking_tariff_segments && Array.isArray(bookingData.outbound_booking_tariff_segments)) {
    for (const segment of bookingData.outbound_booking_tariff_segments) {
      // Check required products
      if (segment?.required_products && Array.isArray(segment.required_products)) {
        originalItem = segment.required_products.find(
          (p: any) => p?.uuid === itemId || p?.id === itemId
        )
        if (originalItem) break
      }

      // Check additional products
      if (!originalItem && segment?.additional_products && Array.isArray(segment.additional_products)) {
        originalItem = segment.additional_products.find(
          (p: any) => p?.uuid === itemId || p?.id === itemId
        )
        if (originalItem) break
      }
    }
  }

  // If not found in outbound, check inbound segments
  if (!originalItem && bookingData?.inbound_booking_tariff_segments && Array.isArray(bookingData.inbound_booking_tariff_segments)) {
    for (const segment of bookingData.inbound_booking_tariff_segments) {
      // Check required products
      if (segment?.required_products && Array.isArray(segment.required_products)) {
        originalItem = segment.required_products.find(
          (p: any) => p?.uuid === itemId || p?.id === itemId
        )
        if (originalItem) break
      }

      // Check additional products
      if (!originalItem && segment?.additional_products && Array.isArray(segment.additional_products)) {
        originalItem = segment.additional_products.find(
          (p: any) => p?.uuid === itemId || p?.id === itemId
        )
        if (originalItem) break
      }
    }
  }
  
  return originalItem
}

/**
 * Helper function to find the original segment for an item
 */
export const findOriginalSegment = (itemId: string, bookingData: any) => {
  
  // Look for the original segment in the booking data outbound segments
  if (bookingData?.outbound_booking_tariff_segments && Array.isArray(bookingData.outbound_booking_tariff_segments)) {
    for (const segment of bookingData.outbound_booking_tariff_segments) {
      // Check required products
      if (segment?.required_products && Array.isArray(segment.required_products) && 
          segment.required_products.some((p: any) => p?.uuid === itemId || p?.id === itemId)) {
        return segment
      }

      // Check additional products
      if (segment?.additional_products && Array.isArray(segment.additional_products) && 
          segment.additional_products.some((p: any) => p?.uuid === itemId || p?.id === itemId)) {
        return segment
      }
    }
  }

  if (bookingData?.inbound_booking_tariff_segments && Array.isArray(bookingData.inbound_booking_tariff_segments)) {
    for (const segment of bookingData.inbound_booking_tariff_segments) {
      // Check required products
      if (segment?.required_products && Array.isArray(segment.required_products) && 
          segment.required_products.some((p: any) => p?.uuid === itemId || p?.id === itemId)) {
        return segment
      }

      // Check additional products
      if (segment?.additional_products && Array.isArray(segment.additional_products) && 
          segment.additional_products.some((p: any) => p?.uuid === itemId || p?.id === itemId)) {
        return segment
      }
    }
  }
  
  return null
}

/**
 * Filters additional products that can be rebooked based on availability and other criteria
 */
// findOriginalItem implementation removed to resolve duplicate declaration error

/**
 * Gets all additional products from segments
 */
export const getAllAdditionalProducts = (
  allSegments: any[] | null | undefined
): any[] => {
  // Safety check for allSegments
  if (!Array.isArray(allSegments)) {
    console.warn('[WARN] getAllAdditionalProducts called with non-array segments:', allSegments);
    return [];
  }
  
  // Get all additional products from all segments
  const allAdditionalProducts = allSegments.flatMap(segment => {
    // Check if segment.additional_products is an array
    const additionalProducts = Array.isArray(segment.additional_products) ? segment.additional_products : [];
    return [...additionalProducts];
  }).filter(product => {
    // Only include products that aren't already cancelled
    return isNotCancelled(product);
  });

  console.log(`[DEBUG] Found ${allAdditionalProducts.length} total additional products`);
  return allAdditionalProducts;
}

/**
 * Identifies products that are not available on the new service and should be cancelled
 */
export const identifyUnavailableProducts = (
  allAdditionalProducts: any[],
  rebookableProducts: any[],
  itemsToReplace: string[] = []
): any[] => {
  // Safety check for inputs
  if (!Array.isArray(allAdditionalProducts) || !Array.isArray(rebookableProducts)) {
    console.warn('[WARN] identifyUnavailableProducts called with invalid inputs:', {
      allAdditionalProductsIsArray: Array.isArray(allAdditionalProducts),
      rebookableProductsIsArray: Array.isArray(rebookableProducts)
    });
    return [];
  }
  
  // Identify products that are not available on the new service and should be cancelled
  const unavailableProducts = allAdditionalProducts.filter(product => {
    // Get product ID
    const productId = product.uuid || product.id;
    if (!productId) return false;
    
    // Check if this product is in the rebookable products list
    const isRebookable = rebookableProducts.some(rebookable => 
      rebookable.uuid === productId || rebookable.id === productId
    );
    
    // If it's not rebookable and not already in itemsToReplace, it should be cancelled
    return !isRebookable && !itemsToReplace.includes(productId);
  });
  
  if (unavailableProducts.length > 0) {
    console.log(`[DEBUG] Found ${unavailableProducts.length} products that are not available on the new service and will be cancelled`);
  }
  
  return unavailableProducts;
}

/**
 * Adds unavailable products to the itemsToReplace list
 */
export const addUnavailableProductsToReplace = (
  unavailableProducts: any[],
  itemsToReplace: string[] = []
): string[] => {
  // Create a new array to avoid modifying the original
  const updatedItemsToReplace = [...itemsToReplace];
  
  unavailableProducts.forEach(product => {
    const productId = product.uuid || product.id;
    const productCode = product.code || product.product_code;
    const serviceId = getProductServiceName(product);
    
    if (productId && !updatedItemsToReplace.includes(productId)) {
      updatedItemsToReplace.push(productId);
      console.log(`[DEBUG] Adding unavailable product to cancellation list: ${productCode} (${productId}) for service ${serviceId}`);
    }
  });
  
  return updatedItemsToReplace;
}

// Import the global tracking set from rebookingRequestBuilder
import { globalUsedExistingItemIds } from '../../services/rebooking/rebookingRequestBuilder';

/**
 * Processes additional products for rebooking and adds them to the API request
 * with detailed logging to identify why products might be excluded
 */
export const processAdditionalProductsForRebooking = (
  additionalProductsToRebook: RebookingAdditionalProduct[],
  apiRequest: any,
  itemsToReplace: string[] = [],
  newJourneyProductsByService?: Map<string, Set<string>>
): void => {
  console.log('===== PROCESSING ADDITIONAL PRODUCTS FOR REBOOKING =====');
  
  if (!additionalProductsToRebook || !Array.isArray(additionalProductsToRebook) || additionalProductsToRebook.length === 0) {
    console.log('[PROCESS] No additional products to process for rebooking');
    return;
  }
  
  console.log(`[PROCESS] Processing ${additionalProductsToRebook.length} additional products for rebooking request`);
  
  // Log the items marked for replacement
  if (itemsToReplace && itemsToReplace.length > 0) {
    console.log(`[PROCESS] Items marked for replacement (${itemsToReplace.length}):`, itemsToReplace);
  } else {
    console.log('[PROCESS] No items marked for replacement');
  }
  
  // Log the available services and products
  if (newJourneyProductsByService && typeof newJourneyProductsByService.forEach === 'function') {
    console.log('[PROCESS] Available services and products for new journey:');
    newJourneyProductsByService.forEach((products, service) => {
      console.log(`[PROCESS] Service: ${service}, Products: ${Array.from(products).join(', ')}`);
    });
  } else {
    console.warn('[PROCESS] WARNING: newJourneyProductsByService is not properly initialized');
  }
  
  // Log all products to be processed
  console.log('[PROCESS] Products to be processed:');
  additionalProductsToRebook.forEach((product, index) => {
    console.log(`[PROCESS] Product ${index + 1}:`, {
      itemId: product.itemId || 'unknown',
      uuid: product.uuid || 'unknown',
      code: product.code || product.product_code || 'unknown',
      departureStation: product.departureStation || 'unknown',
      arrivalStation: product.arrivalStation || 'unknown',
      passenger_ref_id: product.passenger_ref_id || 'unknown'
    });
  });
  
  // Process each product
  additionalProductsToRebook.forEach((product) => {
    const productCode = product.code || product.product_code || 'unknown';
    const itemId = product.itemId || 'unknown';
    const uuid = product.uuid || 'unknown';
    
    console.log(`[PROCESS] Processing product ${productCode} (${itemId})...`);
    
    // Check if product is cancelled - only skip if itemId is defined and in the replace list
    if (itemId !== 'unknown' && Array.isArray(itemsToReplace) && itemsToReplace.includes(itemId)) {
      console.log(`[PROCESS] Product ${productCode} (${itemId}) is marked for replacement - skipping`);
      return;
    }
    
    // Check if product UUID is already used
    if (product.uuid && globalUsedExistingItemIds.has(product.uuid)) {
      console.log(`[PROCESS] Skipping product ${productCode} (${uuid}) as it's already used in a replacement mapping`);
      return;
    }
    
    // Find matching segment with multiple strategies
    console.log(`[PROCESS] Looking for matching segment for product ${productCode} (${itemId})...`);
    console.log(`[PROCESS] Departure: ${product.departureStation}, Arrival: ${product.arrivalStation}`);
    
    // Strategy 1: Try to find exact station match
    let matchingSegment = apiRequest.segments.find((segment: any) => {
      const matches = (
        segment.departure_station_code === product.departureStation &&
        segment.arrival_station_code === product.arrivalStation
      );
      if (matches) {
        console.log(`[PROCESS] Found exact station match segment: ${segment.ref_id} (${segment.departure_station_code} -> ${segment.arrival_station_code})`);
      }
      return matches;
    });
    
    // Strategy 2: Try to find by segment_ref_id if available
    if (!matchingSegment && product.segment_ref_id) {
      console.log(`[PROCESS] Trying to find segment by ref_id: ${product.segment_ref_id}`);
      matchingSegment = apiRequest.segments.find((segment: any) => {
        const matches = segment.ref_id === product.segment_ref_id;
        if (matches) {
          console.log(`[PROCESS] Found segment by ref_id: ${segment.ref_id}`);
        }
        return matches;
      });
    }
    
    // Strategy 3: If no exact match, try partial station match
    if (!matchingSegment && (product.departureStation || product.arrivalStation)) {
      console.log(`[PROCESS] Trying partial station match...`);
      matchingSegment = apiRequest.segments.find((segment: any) => {
        // Try to match either departure or arrival
        const partialMatch = (
          segment.departure_station_code === product.departureStation || 
          segment.arrival_station_code === product.arrivalStation
        );
        if (partialMatch) {
          console.log(`[PROCESS] Found partial station match segment: ${segment.ref_id}`);
        }
        return partialMatch;
      });
    }
    
    // Strategy 4: Use first segment as fallback if no match found and segments exist
    if (!matchingSegment && apiRequest.segments && Array.isArray(apiRequest.segments) && apiRequest.segments.length > 0) {
      console.log(`[PROCESS] No matching segment found, using first segment as fallback`);
      matchingSegment = apiRequest.segments[0];
      console.log(`[PROCESS] Using fallback segment: ${matchingSegment.ref_id} (${matchingSegment.departure_station_code} -> ${matchingSegment.arrival_station_code})`);
    }
    
    if (!matchingSegment) {
      console.log(`[PROCESS] No matching segment found for product ${productCode} (${itemId}) with stations ${product.departureStation || 'unknown'} -> ${product.arrivalStation || 'unknown'}`);
      console.log('[PROCESS] Available segments in request:');
      if (apiRequest.segments && Array.isArray(apiRequest.segments)) {
        apiRequest.segments.forEach((segment: any, index: number) => {
          console.log(`[PROCESS] Segment ${index + 1}: ${segment.departure_station_code || 'unknown'} -> ${segment.arrival_station_code || 'unknown'} (${segment.service_name || 'unknown service'})`);
        });
      } else {
        console.log('[PROCESS] No segments available in the request');
      }
      return;
    }
    
    // Check product availability with more flexible approach
    let isProductAvailable = true;
    
    if (newJourneyProductsByService && productCode !== 'unknown') {
      // Extract service name with multiple strategies
      let serviceId;
      
      // Strategy 1: Try to get service name from segment
      if (matchingSegment.service_name) {
        serviceId = matchingSegment.service_name;
        console.log(`[PROCESS] Using service name from segment: ${serviceId}`);
      }
      // Strategy 2: Try to parse service identifier
      else if (matchingSegment.service_identifier) {
        const parts = matchingSegment.service_identifier.split('|');
        if (parts.length >= 2) {
          serviceId = parts[1];
          console.log(`[PROCESS] Extracted service name from segment identifier: ${serviceId}`);
        }
      }
      // Strategy 3: Try to get service name from main product
      else if (Array.isArray(matchingSegment.required_products) && matchingSegment.required_products.length > 0) {
        const mainProduct = matchingSegment.required_products[0];
        if (mainProduct.service_name) {
          serviceId = mainProduct.service_name;
          console.log(`[PROCESS] Using service name from main product: ${serviceId}`);
        } else if (mainProduct.service_identifier) {
          const parts = mainProduct.service_identifier.split('|');
          if (parts.length >= 2) {
            serviceId = parts[1];
            console.log(`[PROCESS] Extracted service name from main product identifier: ${serviceId}`);
          }
        }
      }
      
      // Store the service ID in the product for later use
      if (serviceId) {
        product._extracted_service_name = serviceId;
        console.log(`[PROCESS] Stored extracted service name ${serviceId} in product for availability check`);
      }
      
      // Enhanced logging for luggage and bike products
      const isLuggageOrBikeProduct = productCode.startsWith('LU') || productCode.startsWith('BK');
      if (isLuggageOrBikeProduct) {
        console.log(`[PROCESS] Checking exact match for ${productCode} (${itemId}) as it's a luggage or bike product`);
        
        // Check if this exact product code exists in any service
        let exactProductFoundInAnyService = false;
        
        newJourneyProductsByService.forEach((products, service) => {
          if (products.has(productCode)) {
            exactProductFoundInAnyService = true;
            console.log(`[PROCESS] Exact product ${productCode} found in service ${service}`);
          }
        });
        
        // Log detailed information about available products
        console.log(`[PROCESS] Available products in all services:`);
        newJourneyProductsByService.forEach((products, service) => {
          console.log(`[PROCESS] Service ${service} products: ${Array.from(products).join(', ')}`);
        });
        
        // We don't set isProductAvailable here - we'll continue with the normal availability check
        // This is just for enhanced logging
      }
      
      // Regular product availability check
      if (serviceId) {
        console.log(`[PROCESS] Checking if product ${productCode} is available on service ${serviceId}...`);
        
        const availableProducts = newJourneyProductsByService.get(serviceId);
        
        if (availableProducts && availableProducts.has(productCode)) {
          console.log(`[PROCESS] Product ${productCode} is AVAILABLE on service ${serviceId}`);
          isProductAvailable = true;
        } else {
          // If not available on this service, check if it's available on any service
          console.log(`[PROCESS] Product ${productCode} not available on service ${serviceId}, checking all services...`);
          
          let foundInAnyService = false;
          newJourneyProductsByService.forEach((products, service) => {
            if (products.has(productCode)) {
              console.log(`[PROCESS] Product ${productCode} found in service ${service}`);
              foundInAnyService = true;
            }
          });
          
          isProductAvailable = foundInAnyService;
          
          if (!isProductAvailable) {
            console.log(`[PROCESS] Product ${productCode} is NOT AVAILABLE on any service`);
          }
        }
      } else {
        // If no service name, check all services
        console.log(`[PROCESS] No service name for segment, checking if product ${productCode} is available on any service...`);
        
        let foundInAnyService = false;
        newJourneyProductsByService.forEach((products, service) => {
          if (products.has(productCode)) {
            console.log(`[PROCESS] Product ${productCode} found in service ${service}`);
            foundInAnyService = true;
          }
        });
        
        isProductAvailable = foundInAnyService;
        
        if (!isProductAvailable) {
          console.log(`[PROCESS] Product ${productCode} is NOT AVAILABLE on any service`);
        }
      }
    } else {
      console.log(`[PROCESS] Cannot check product availability - missing data:`, {
        hasProductsByService: !!newJourneyProductsByService,
        serviceNameExists: !!matchingSegment?.service_name,
        productCodeExists: productCode !== 'unknown'
      });
      // Assume available if we can't check properly
      isProductAvailable = true;
    }
    
    // Find the correct passenger reference
    let passengerRefId = product.passenger_ref_id;
    
    if (!passengerRefId && apiRequest.passengers && Array.isArray(apiRequest.passengers) && apiRequest.passengers.length > 0) {
      // Try to find a matching passenger by UUID or ID
      const matchingPassenger = apiRequest.passengers.find((p: any) => 
        (product.uuid && p.uuid === product.uuid) || 
        (p.id === product.passenger_id));
      
      if (matchingPassenger) {
        passengerRefId = matchingPassenger.ref_id;
        console.log(`[PROCESS] Found matching passenger: ${passengerRefId}`);
      } else {
        // Default to first passenger if no match
        passengerRefId = apiRequest.passengers[0].ref_id;
        console.log(`[PROCESS] No matching passenger found, using first passenger: ${passengerRefId}`);
      }
    }
    
    // Add product to request if available
    if (isProductAvailable) {
      const segmentRefId = matchingSegment.ref_id;
      const uniqueId = uuidv4().substring(0, 8);
      
      const additionalItem = {
        ref_id: `item_${segmentRefId}_${productCode || 'unknown'}_${uniqueId}`,
        passenger_ref_id: passengerRefId || (apiRequest.passengers && Array.isArray(apiRequest.passengers) && apiRequest.passengers.length > 0 ? apiRequest.passengers[0]?.ref_id : 'passenger_1'),
        segment_ref_id: segmentRefId,
        product_code: productCode || 'UNKNOWN',
        tariff_code: product.tariff_code || productCode || 'UNKNOWN'
      };
      
      console.log(`[PROCESS] ADDING additional product to request:`, additionalItem);
      apiRequest.items.push(additionalItem);
      
      if (product.uuid) {
        globalUsedExistingItemIds.add(product.uuid);
        console.log(`[PROCESS] Marked product UUID ${product.uuid} as used in global tracking set`);
      }
    } else {
      console.log(`[PROCESS] Product ${productCode} (${itemId}) is NOT available for the matching segment - skipping`);
    }
  });
  
  // Log summary of luggage and bike products processing
  const luggageBikeProducts = additionalProductsToRebook.filter(p => {
    const code = p.code || p.product_code || '';
    return code.startsWith('LU') || code.startsWith('BK');
  });
  
  if (luggageBikeProducts.length > 0) {
    console.log(`[PROCESS] Summary of luggage/bike products processing:`);
    console.log(`[PROCESS] Total luggage/bike products: ${luggageBikeProducts.length}`);
    
    // Count how many were added to the request
    const addedLuggageBike = apiRequest.items.filter(item => {
      const code = item.product_code || '';
      return code.startsWith('LU') || code.startsWith('BK');
    });
    
    console.log(`[PROCESS] Luggage/bike products added to request: ${addedLuggageBike.length}`);
    
    if (addedLuggageBike.length > 0) {
      console.log(`[PROCESS] Added luggage/bike products:`, addedLuggageBike.map(item => ({
        ref_id: item.ref_id,
        product_code: item.product_code,
        segment_ref_id: item.segment_ref_id
      })));
    }
    
    if (addedLuggageBike.length < luggageBikeProducts.length) {
      console.log(`[PROCESS] WARNING: ${luggageBikeProducts.length - addedLuggageBike.length} luggage/bike products were not added to the request`);
    }
  }
  
  console.log(`[PROCESS] Finished processing additional products. Total items in request: ${apiRequest.items.length}`);
}

/**
 * Filters additional products that can be rebooked based on availability and other criteria
 */
export const filterRebookableProducts = (
  allSegments: any[] | null | undefined,
  newJourneyProductsByService: Map<string, Set<string>>,
  bookingData?: any
): RebookingAdditionalProduct[] => {
  console.log('===== FILTERING REBOOKABLE ADDITIONAL PRODUCTS =====');
  
  // Safety check for allSegments
  if (!Array.isArray(allSegments)) {
    console.warn('[WARN] filterRebookableProducts called with non-array segments:', allSegments);
    return [];
  }
  
  console.log(`Found ${allSegments.length} segments to check for additional products`);
  
  // Log available services and products in new journey
  console.log('Available services in new journey:');
  if (newJourneyProductsByService && typeof newJourneyProductsByService.forEach === 'function') {
    newJourneyProductsByService.forEach((products, service) => {
      console.log(`Service: ${service}, Products: ${Array.from(products).join(', ')}`);
    });
  } else {
    console.warn('[WARN] newJourneyProductsByService is not properly initialized or empty');
  }

  const allAdditionalProducts = allSegments.flatMap(segment => {
    // Check if segment.additional_products is an array
    const additionalProducts = Array.isArray(segment.additional_products) ? segment.additional_products : [];
    console.log(`Segment ${segment.id || 'unknown'} has ${additionalProducts.length} additional products`);
    
    // Log details of each additional product in the segment
    if (additionalProducts.length > 0) {
      additionalProducts.forEach((product, index) => {
        console.log(`[DETAIL] Segment ${segment.id || 'unknown'} - Product ${index + 1}:`, {
          id: product.id || product.item_id || 'unknown',
          code: product.code || product.product_code || 'unknown',
          name: product.name || 'unknown',
          departureStation: product.departure_station || product.departureStation || 'unknown',
          arrivalStation: product.arrival_station || product.arrivalStation || 'unknown',
          serviceIdentifier: product.service_identifier || 'unknown',
          status: product.status || 'unknown',
          can_be_rebooked: product.can_be_rebooked || false,
          passenger_id: product.passenger_id || product.passenger_uuid || 'unknown',
          passenger_ref_id: product.passenger_ref_id || 'unknown',
          itemId: product.itemId || product.item_id || product.id || 'unknown',
          uuid: product.uuid || 'unknown'
        });
      });
    }
    
    return [...additionalProducts];
  });
  
  console.log(`Total additional products found: ${allAdditionalProducts.length}`);
  
  // Ensure we have an array before filtering
  if (!Array.isArray(allAdditionalProducts)) {
    console.warn('[WARN] allAdditionalProducts is not an array:', allAdditionalProducts);
    return [];
  }
  
  const rebookableProducts = allAdditionalProducts.filter((product) => {
    const serviceId = getProductServiceName(product, bookingData || allSegments?.bookingData);
    const productCode = product.code || product.product_code || 'unknown';
    const itemId = product.item_id || product.id || 'unknown';
    
    console.log(`[FILTER] Evaluating product ${productCode} (${itemId}) for rebooking:`, {
      serviceId,
      productCode,
      itemId,
      status: product.status || 'unknown',
      can_be_rebooked: product.can_be_rebooked
    });
    
    if (!isNotCancelled(product)) {
      console.log(`[FILTER] Product ${productCode} (${itemId}) REJECTED - already cancelled. Status: ${product.status}`);
      return false;
    }
    
    if (product.can_be_rebooked !== true) {
      console.log(`[FILTER] Product ${productCode} (${itemId}) REJECTED - cannot be rebooked. can_be_rebooked: ${product.can_be_rebooked}`);
      return false;
    }
    
    // Check product availability with detailed logging
    // If we don't have a serviceId, try to find a matching service by product code
    if (!serviceId && productCode !== 'unknown') {
      console.log(`[FILTER] No service ID for product ${productCode}, checking if product exists in any service...`);
      let foundInAnyService = false;
      
      newJourneyProductsByService.forEach((products, service) => {
        if (products.has(productCode)) {
          foundInAnyService = true;
          console.log(`[FILTER] Product ${productCode} found in service ${service}`);
        }
      });
      
      if (foundInAnyService) {
        console.log(`[FILTER] Product ${productCode} (${itemId}) ACCEPTED - found in at least one service`);
        return true;
      }
    }
    
    // Enhanced logging for luggage and bike products to help debug issues
    if (productCode.startsWith('LU') || productCode.startsWith('BK')) {
      console.log(`[FILTER] Checking exact match for ${productCode} (${itemId}) as it's a luggage or bike product`);
      
      // Check if this exact product code exists in any service
      let exactProductFoundInAnyService = false;
      
      newJourneyProductsByService.forEach((products, service) => {
        if (products.has(productCode)) {
          exactProductFoundInAnyService = true;
          console.log(`[FILTER] Exact product ${productCode} found in service ${service}`);
        }
      });
      
      // Log detailed information about available products
      console.log(`[FILTER] Available products in all services:`);
      newJourneyProductsByService.forEach((products, service) => {
        console.log(`[FILTER] Service ${service} products: ${Array.from(products).join(', ')}`);
      });
      
      // We don't return here - we'll continue with the normal availability check
    }
    
    // Regular availability check with booking data for service lookup
    // Store the service ID we found earlier to ensure it's used in availability check
    if (serviceId) {
      // Ensure the service ID is available in the product for the availability check
      product._extracted_service_name = serviceId;
    }
    const isAvailable = isProductAvailableForService(product, newJourneyProductsByService, bookingData || allSegments?.bookingData);
    if (!isAvailable) {
      console.log(`[FILTER] Product ${productCode} (${itemId}) not available for service ${serviceId}, checking alternatives...`);
      
      // Enhanced logging for luggage and bike products
      if (productCode.startsWith('LU') || productCode.startsWith('BK')) {
        console.log(`[FILTER] Detailed check for luggage/bike product ${productCode} (${itemId})`);
        
        // Log all available products for better debugging
        console.log(`[FILTER] Available products in all services for comparison:`);
        newJourneyProductsByService.forEach((products, service) => {
          console.log(`[FILTER] Service ${service} products: ${Array.from(products).join(', ')}`);
        });
      }
      
      // Detailed logging for rejection
      console.log(`[FILTER] Product ${productCode} (${itemId}) REJECTED - not available for service ${serviceId}`);
      console.log(`   - Service exists in map: ${serviceId ? newJourneyProductsByService.has(serviceId) : 'No service ID'}`);
      if (serviceId && newJourneyProductsByService.has(serviceId)) {
        const availableProducts = newJourneyProductsByService.get(serviceId);
        console.log(`   - Available products for service ${serviceId}: ${Array.from(availableProducts || []).join(', ')}`);
        console.log(`   - Product code ${productCode} exists for service: ${availableProducts?.has(productCode)}`);
      } else {
        console.log(`   - Service ${serviceId} not found in newJourneyProductsByService map`);  
      }
      return false;
    }
    
    console.log(`[FILTER] Product ${productCode} (${itemId}) ACCEPTED for rebooking on service ${serviceId}`);
    return true;
  });
  
  // Log summary of all products and which ones were filtered out
  console.log(`[SUMMARY] ${rebookableProducts.length} out of ${allAdditionalProducts.length} products are eligible for rebooking`);
  
  // Log specifically about luggage and bike products
  const luggageAndBikeProducts = allAdditionalProducts.filter(p => {
    const code = p.code || p.product_code || '';
    return code.startsWith('LU') || code.startsWith('BK');
  });
  
  const rebookableLuggageAndBike = rebookableProducts.filter(p => {
    const code = p.code || p.product_code || '';
    return code.startsWith('LU') || code.startsWith('BK');
  });
  
  console.log(`[SUMMARY] Found ${luggageAndBikeProducts.length} luggage/bike products, ${rebookableLuggageAndBike.length} are rebookable`);
  
  if (luggageAndBikeProducts.length > 0) {
    console.log('[SUMMARY] All luggage/bike products:', luggageAndBikeProducts.map(p => ({
      id: p.id || p.item_id || 'unknown',
      code: p.code || p.product_code || 'unknown',
      serviceId: getProductServiceName(p, bookingData || allSegments?.bookingData),
      status: p.status || 'unknown',
      can_be_rebooked: p.can_be_rebooked || false
    })));
  }
  
  if (rebookableLuggageAndBike.length > 0) {
    console.log('[SUMMARY] Rebookable luggage/bike products:', rebookableLuggageAndBike.map(p => ({
      id: p.id || p.item_id || 'unknown',
      code: p.code || p.product_code || 'unknown',
      serviceId: getProductServiceName(p, bookingData || allSegments?.bookingData)
    })));
  }
  
  if (rebookableProducts.length > 0) {
    console.log('[SUMMARY] All rebookable products:', rebookableProducts.map(p => ({ 
      id: p.id || p.item_id, 
      code: p.code || p.product_code,
      serviceId: getProductServiceName(p, bookingData || allSegments?.bookingData)
    })));
  }
  
  // Transform the products to match the RebookingAdditionalProduct interface from rebookingTypes.ts
  const transformedProducts: RebookingAdditionalProduct[] = rebookableProducts.map(product => ({
    passenger_ref_id: product.passenger_id || product.passenger_ref_id || '',
    segment_ref_id: product.segment_id || product.segment_ref_id || '',
    product_code: product.code || product.product_code || '',
    tariff_code: product.tariff_code || '',
    price: product.price,
    departureStation: product.departure_station || product.departureStation,
    arrivalStation: product.arrival_station || product.arrivalStation,
    itemId: product.itemId || product.item_id || product.id,
    uuid: product.uuid
  }));
  
  return transformedProducts;
}
